import { useEffect, useMemo, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Field, ErrorBox } from "@/components/common";
import { useMembers } from "@/components/common/OwnerSelect";
import { useAuth } from "@/lib/auth";
import { Sparkles, X, ArrowLeft, ArrowRight, Check, Lightbulb, Pencil, Wand2, PenLine, Target, Users, ShieldQuestion, UserCog } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", offer_id: null, description: "", geography: [], business_types: [], size_preferences: "", required_conditions: [], preferences: [], things_to_verify: [], exclusions: [], buyer_roles: [], not_determinable_from_public_info: [], owner_id: null, assigned_user_ids: [], research_budget_usd: 5, target_accepted: 20, status: "active" };

const STEP_DEFS = [
  { key: "mode", label: "Start", title: "Start your ICP", subtitle: "Describe your market and let AI draft it, or build it yourself.", icon: Wand2 },
  { key: "basics", label: "Basics", title: "The basics", subtitle: "Name it, link an offer and add a one-line description.", icon: PenLine },
  { key: "fit", label: "Fit signals", title: "Fit signals", subtitle: "What makes an account a great fit — or a hard no.", icon: Target },
  { key: "people", label: "Market", title: "People & market", subtitle: "Where they are and who makes the decision.", icon: Users },
  { key: "verify", label: "Verify", title: "Verification notes", subtitle: "Internal prompts for your discovery calls.", icon: ShieldQuestion },
  { key: "assign", label: "Ownership", title: "Ownership & targets", subtitle: "Who owns this ICP and how hard to push it.", icon: UserCog },
  { key: "review", label: "Review", title: "Review & create", subtitle: "Confirm everything before saving.", icon: Check },
];

const FIT_LISTS = [
  ["required_conditions", "Must-have signals", "If this isn't true, the account is not a fit.", "icp-required-conditions-chip-editor"],
  ["preferences", "Nice-to-have signals", "Improves win rate, but not required.", "icp-preferences-chip-editor"],
  ["exclusions", "Disqualifiers", "Accounts matching these are skipped automatically.", "icp-exclusions-chip-editor"],
];
const PEOPLE_LISTS = [
  ["geography", "Geography", "Regions, countries or cities.", "icp-geography-chip-editor"],
  ["business_types", "Business types", "e.g. manufacturer, hotel, SaaS.", "icp-business-types-chip-editor"],
  ["buyer_roles", "Likely buyer roles", "Include the champion and the economic buyer.", "icp-buyer-roles-chip-editor"],
];
const VERIFY_LISTS = [
  ["things_to_verify", "Things to verify (internal)", "Research prompts — never used as search filters.", "icp-things-to-verify-chip-editor"],
  ["not_determinable_from_public_info", "Unknown from public info", "Shown so nobody assumes these are true.", "icp-not-determinable-chip-editor"],
];

const TIPS = {
  mode: ["Describe your best-fit customer in 2–4 sentences and let AI draft the structure.", "You stay in control — every field stays editable afterwards.", "Prefer a blank canvas? Switch to Build manually."],
  basics: ["Give the ICP a memorable name like 'Independent food makers (Ireland)'.", "Link the offer you actually sell to sharpen targeting.", "A one-line description keeps the whole team aligned."],
  fit: ["Must-have = required to qualify.", "Nice-to-have = improves win rate.", "Disqualifiers remove poor-fit accounts automatically.", "Keep each signal short (2–6 words)."],
  people: ["Geography can be regions, countries or cities.", "List the business types you sell into.", "Name the roles who decide and champion the deal."],
  verify: ["These are internal prompts for discovery calls.", "They are NEVER used as search filters.", "Examples: 'Who owns the budget?', 'Renewal date?'."],
  assign: ["The owner is accountable for this ICP.", "Assigned salespeople pick up accepted accounts.", "Budget and target help plan discovery runs."],
  review: ["Check each section — tap Edit to jump back.", "Nothing is saved until you press Create.", "You can refine the ICP anytime later."],
};

function ListEditor({ label, hint, items, onChange, testId }) {
  const [draft, setDraft] = useState("");
  const addMany = (raw) => { const parts = raw.split(/[\n,]/).map((x) => x.trim()).filter(Boolean); if (parts.length) onChange([...(items || []), ...parts]); };
  const add = () => { const v = draft.trim(); if (!v) return; addMany(v); setDraft(""); };
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium text-foreground">{label}</div>
      {hint && <div className="meta">{hint}</div>}
      {(items || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {items.map((it, i) => (
            <span key={i} className="chip chip-muted max-w-full transition-[opacity,transform] duration-150">
              <span className="truncate">{it}</span>
              <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label={`Remove ${it}`} className="ml-1 hover:text-[hsl(var(--danger))]"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); add(); }
            else if (e.key === "Backspace" && !draft && (items || []).length) onChange(items.slice(0, -1));
          }}
          onPaste={(e) => { const t = e.clipboardData.getData("text"); if (/[\n,]/.test(t)) { e.preventDefault(); addMany(t); } }}
          placeholder="Type and press Enter…"
          data-testid={testId}
        />
        <Button type="button" variant="outline" size="sm" onClick={add}>Add</Button>
      </div>
    </div>
  );
}

function OfferSelect({ offers, value, onChange, testId }) {
  return (
    <Select value={value || "__none"} onValueChange={(v) => onChange(v === "__none" ? null : v)}>
      <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="__none">No specific offer</SelectItem>{offers.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function ReviewSection({ title, onEdit, testId, children }) {
  return (
    <div className="panel p-4 space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="section-title">{title}</span>
        <Button type="button" variant="ghost" size="sm" onClick={onEdit}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
      </div>
      {children}
    </div>
  );
}

const Chips = ({ items }) => (items || []).length
  ? <div className="flex flex-wrap gap-1.5">{items.map((it, i) => <span key={i} className="chip chip-muted max-w-full"><span className="truncate">{it}</span></span>)}</div>
  : <span className="meta">— Not set</span>;

const Line = ({ label, value }) => (
  <div className="flex gap-2 text-sm"><span className="text-muted-foreground w-28 shrink-0">{label}</span><span className="min-w-0">{value || <span className="meta">— Not set</span>}</span></div>
);

export function ICPEditorDialog({ open, onOpenChange, initial, onSaved }) {
  const { can } = useAuth();
  const members = useMembers();
  const [offers, setOffers] = useState([]);
  const editing = !!initial?.id;
  const steps = useMemo(() => (editing ? STEP_DEFS.filter((s) => s.key !== "mode") : STEP_DEFS), [editing]);
  const [stepIdx, setStepIdx] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  const [icp, setIcp] = useState(EMPTY);
  const [desc, setDesc] = useState("");
  const [aiMode, setAiMode] = useState("ai");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [aiMeta, setAiMeta] = useState(null);
  const headingRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/offers").then((r) => setOffers(r.data)).catch(() => {});
    setIcp(initial ? { ...EMPTY, ...initial } : EMPTY);
    setDesc(initial?.description || "");
    setStepIdx(0); setMaxReached(0); setError(null); setAiMeta(null); setAiMode("ai");
  }, [open, initial]);

  const cur = steps[stepIdx] || steps[0];
  const isLast = stepIdx === steps.length - 1;
  const set = (k, v) => setIcp((p) => ({ ...p, [k]: v }));
  const offerName = offers.find((o) => o.id === icp.offer_id)?.name;
  const gotoKey = (key) => { const i = steps.findIndex((s) => s.key === key); if (i >= 0 && i <= maxReached) { setError(null); setStepIdx(i); } };

  useEffect(() => { if (open && headingRef.current) headingRef.current.focus(); }, [stepIdx, open]);

  const parse = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/icps/parse", { description: desc, offer_id: icp.offer_id || null });
      setIcp((prev) => ({ ...prev, ...data.icp, offer_id: icp.offer_id || null }));
      setAiMeta(data.ai);
      const bi = steps.findIndex((s) => s.key === "basics");
      setStepIdx(bi); setMaxReached((m) => Math.max(m, bi));
      toast.success("Draft created — review and refine.");
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const validateStep = (key) => {
    if (key === "basics" && (!icp.name || icp.name.trim().length < 2)) return "Give your ICP a name (at least 2 characters).";
    if (key === "assign") {
      if (!(Number(icp.research_budget_usd) >= 0.1)) return "Research budget must be at least 0.1.";
      if (!(Number(icp.target_accepted) >= 1)) return "Target accepted must be at least 1.";
    }
    return null;
  };

  const next = () => {
    const err = validateStep(cur.key);
    if (err) { setError(err); return; }
    setError(null);
    const ni = Math.min(stepIdx + 1, steps.length - 1);
    setStepIdx(ni); setMaxReached((m) => Math.max(m, ni));
  };
  const back = () => { setError(null); setStepIdx((i) => Math.max(0, i - 1)); };
  const gotoIdx = (i) => { if (i <= maxReached) { setError(null); setStepIdx(i); } };

  const save = async () => {
    for (let i = 0; i < steps.length; i++) { const e = validateStep(steps[i].key); if (e) { setError(e); setStepIdx(i); setMaxReached((m) => Math.max(m, i)); return; } }
    setBusy(true); setError(null);
    const payload = { ...icp, offer_id: icp.offer_id || null, owner_id: icp.owner_id || null, research_budget_usd: Number(icp.research_budget_usd), target_accepted: Number(icp.target_accepted) };
    ["id", "version", "can_edit", "accepted_count", "runs_count", "pending_suggestions", "history", "org_id", "created_at", "updated_at", "created_by"].forEach((k) => delete payload[k]);
    try {
      if (editing) await api.put(`/icps/${initial.id}`, payload); else await api.post("/icps", payload);
      toast.success(editing ? "ICP updated" : "ICP created"); onSaved?.(); onOpenChange(false);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const renderStep = () => {
    switch (cur.key) {
      case "mode":
        return (
          <Tabs value={aiMode} onValueChange={setAiMode} data-testid="icp-wizard-mode-tabs">
            <TabsList className="grid grid-cols-2 w-full max-w-sm"><TabsTrigger value="ai" data-testid="icp-wizard-tab-ai"><Wand2 className="h-4 w-4 mr-1.5" />Describe with AI</TabsTrigger><TabsTrigger value="manual" data-testid="icp-wizard-tab-manual"><PenLine className="h-4 w-4 mr-1.5" />Build manually</TabsTrigger></TabsList>
            <TabsContent value="ai" className="space-y-4 pt-4">
              <Field label="Relevant offer (optional)"><OfferSelect offers={offers} value={icp.offer_id} onChange={(v) => set("offer_id", v)} testId="icp-mode-offer-select" /></Field>
              <Field label="Describe your best-fit customer">
                <Textarea rows={6} value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="icp-wizard-ai-description-textarea" placeholder="Describe your best-fit customer in 2–4 sentences. e.g. Independent food manufacturers in Ireland selling into wholesale or retail, several product lines, showing growth. Exclude existing customers and pure retailers." />
              </Field>
              <div className="panel p-3 flex items-start gap-2 text-sm"><Sparkles className="h-4 w-4 mt-0.5 text-primary shrink-0" /><span className="text-muted-foreground">The AI drafts a structured, editable profile from your description. Nothing is saved until you press Create.</span></div>
              {busy && <div className="space-y-2"><Skeleton className="h-4 w-2/3" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-1/2" /></div>}
            </TabsContent>
            <TabsContent value="manual" className="pt-4"><div className="panel p-4"><p className="text-sm text-muted-foreground">Prefer to fill it in yourself? Press <span className="font-medium text-foreground">Start building</span> and we'll walk you through each part, one short step at a time.</p></div></TabsContent>
          </Tabs>
        );
      case "basics":
        return (
          <div className="space-y-4">
            {aiMeta && <div className="meta">Drafted by {aiMeta.provider}/{aiMeta.model} ({typeof aiMeta.cost_usd === "number" ? `$${aiMeta.cost_usd.toFixed(4)}` : ""}). Edit anything freely.</div>}
            <Field label="Name"><Input value={icp.name} onChange={(e) => set("name", e.target.value)} data-testid="icp-name-input" placeholder="e.g. Independent food makers (Ireland)" /></Field>
            <Field label="Relevant offer (optional)"><OfferSelect offers={offers} value={icp.offer_id} onChange={(v) => set("offer_id", v)} testId="icp-offer-select" /></Field>
            <Field label="Description (plain English)"><Textarea rows={3} value={icp.description} onChange={(e) => set("description", e.target.value)} data-testid="icp-description-textarea" placeholder="One or two lines the whole team can align on." /></Field>
          </div>
        );
      case "fit":
        return (
          <div className="space-y-4">
            <Field label="Size preferences"><Input value={icp.size_preferences || ""} onChange={(e) => set("size_preferences", e.target.value)} data-testid="icp-size-preferences-input" placeholder="e.g. SME, roughly 10–200 staff" /></Field>
            {FIT_LISTS.map(([k, label, hint, tid]) => <ListEditor key={k} label={label} hint={hint} items={icp[k]} onChange={(v) => set(k, v)} testId={tid} />)}
          </div>
        );
      case "people":
        return <div className="space-y-4">{PEOPLE_LISTS.map(([k, label, hint, tid]) => <ListEditor key={k} label={label} hint={hint} items={icp[k]} onChange={(v) => set(k, v)} testId={tid} />)}</div>;
      case "verify":
        return (
          <div className="space-y-4">
            <div className="panel p-3 flex items-start gap-2 text-sm border-l-2 border-[hsl(var(--warning))]"><ShieldQuestion className="h-4 w-4 mt-0.5 text-[hsl(var(--warning))] shrink-0" /><span className="text-muted-foreground">These notes are internal prompts for your sales conversations. They are never used as discovery search filters.</span></div>
            {VERIFY_LISTS.map(([k, label, hint, tid]) => <ListEditor key={k} label={label} hint={hint} items={icp[k]} onChange={(v) => set(k, v)} testId={tid} />)}
          </div>
        );
      case "assign":
        return (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="ICP owner">
                <Select value={icp.owner_id || "__me"} onValueChange={(v) => set("owner_id", v === "__me" ? null : v)} disabled={!can("icp.assign")}><SelectTrigger data-testid="icp-owner-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="__me">Me</SelectItem>{members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email}</SelectItem>)}</SelectContent></Select>
              </Field>
              <Field label="Assigned salespeople" hint="Accepted accounts route to a single assignee automatically">
                <div className="flex flex-wrap gap-1.5" data-testid="icp-assigned-salespeople-chip-editor">
                  {members.filter((m) => m.is_sales_eligible).map((m) => {
                    const on = icp.assigned_user_ids?.includes(m.user_id);
                    return <button type="button" key={m.user_id} disabled={!can("icp.assign")} onClick={() => set("assigned_user_ids", on ? icp.assigned_user_ids.filter((x) => x !== m.user_id) : [...(icp.assigned_user_ids || []), m.user_id])} className={`chip ${on ? "chip-info" : "chip-muted"}`} data-testid={`icp-assign-${m.user_id}`}>{m.name || m.email}</button>;
                  })}
                  {members.filter((m) => m.is_sales_eligible).length === 0 && <span className="meta">No eligible salespeople yet.</span>}
                </div>
              </Field>
              <Field label="Research budget per run (USD)"><Input type="number" step="0.5" min="0.1" value={icp.research_budget_usd} onChange={(e) => set("research_budget_usd", e.target.value)} data-testid="icp-research-budget-input" /></Field>
              <Field label="Target accepted prospects"><Input type="number" min="1" value={icp.target_accepted} onChange={(e) => set("target_accepted", e.target.value)} data-testid="icp-target-accepted-input" /></Field>
            </div>
          </div>
        );
      case "review":
        return (
          <div className="space-y-3">
            <ReviewSection title="Basics" testId="icp-review-section-basics" onEdit={() => gotoKey("basics")}>
              <Line label="Name" value={icp.name} /><Line label="Offer" value={offerName} /><Line label="Description" value={icp.description} />
            </ReviewSection>
            <ReviewSection title="Fit signals" testId="icp-review-section-fit-signals" onEdit={() => gotoKey("fit")}>
              <Line label="Size" value={icp.size_preferences} />
              <div className="text-sm"><div className="text-muted-foreground mb-1">Must-have</div><Chips items={icp.required_conditions} /></div>
              <div className="text-sm"><div className="text-muted-foreground mb-1 mt-2">Nice-to-have</div><Chips items={icp.preferences} /></div>
              <div className="text-sm"><div className="text-muted-foreground mb-1 mt-2">Disqualifiers</div><Chips items={icp.exclusions} /></div>
            </ReviewSection>
            <ReviewSection title="People & market" testId="icp-review-section-people-market" onEdit={() => gotoKey("people")}>
              <div className="text-sm"><div className="text-muted-foreground mb-1">Geography</div><Chips items={icp.geography} /></div>
              <div className="text-sm"><div className="text-muted-foreground mb-1 mt-2">Business types</div><Chips items={icp.business_types} /></div>
              <div className="text-sm"><div className="text-muted-foreground mb-1 mt-2">Buyer roles</div><Chips items={icp.buyer_roles} /></div>
            </ReviewSection>
            <ReviewSection title="Verification notes" testId="icp-review-section-verification" onEdit={() => gotoKey("verify")}>
              <div className="text-sm"><div className="text-muted-foreground mb-1">To verify</div><Chips items={icp.things_to_verify} /></div>
              <div className="text-sm"><div className="text-muted-foreground mb-1 mt-2">Unknown from public info</div><Chips items={icp.not_determinable_from_public_info} /></div>
            </ReviewSection>
            <ReviewSection title="Ownership & targets" testId="icp-review-section-assignment" onEdit={() => gotoKey("assign")}>
              <Line label="Owner" value={members.find((m) => m.user_id === icp.owner_id)?.name || "Me"} />
              <Line label="Budget/run" value={`$${Number(icp.research_budget_usd).toFixed(2)}`} />
              <Line label="Target" value={`${icp.target_accepted} accepted`} />
            </ReviewSection>
          </div>
        );
      default: return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[min(960px,95vw)] p-0 gap-0 max-h-[92vh] flex flex-col" data-testid="icp-wizard">
        <DialogHeader className="p-5 pb-3 border-b border-border space-y-3 text-left">
          <div>
            <DialogTitle className="font-heading text-xl">{editing ? "Edit ICP" : "Build an ideal customer profile"}</DialogTitle>
            <DialogDescription className="meta">{cur.subtitle}</DialogDescription>
          </div>
          <div className="hidden md:flex items-center gap-1.5 flex-wrap" data-testid="icp-wizard-stepper">
            {steps.map((s, i) => {
              const state = i === stepIdx ? "chip-info" : i < stepIdx ? "chip-success" : "chip-muted";
              const Icon = s.icon;
              return (
                <button key={s.key} type="button" onClick={() => gotoIdx(i)} disabled={i > maxReached} aria-current={i === stepIdx ? "step" : undefined}
                  className={`chip ${state} ${i > maxReached ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                  {i < stepIdx ? <Check className="h-3 w-3" /> : <Icon className="h-3 w-3" />}{s.label}
                </button>
              );
            })}
          </div>
          <div className="md:hidden space-y-1">
            <div className="flex justify-between text-xs"><span className="meta">Step {stepIdx + 1} of {steps.length}</span><span className="meta">{cur.label}</span></div>
            <Progress value={(stepIdx / (steps.length - 1)) * 100} data-testid="icp-wizard-progress" />
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0">
          <div className="p-5 md:grid md:grid-cols-12 md:gap-6">
            <div className="md:col-span-8 space-y-4 fade-up" key={cur.key}>
              <h2 ref={headingRef} tabIndex={-1} className="section-title outline-none">{cur.title}</h2>
              {error && <div role="alert" data-testid="icp-wizard-step-error"><ErrorBox error={error} /></div>}
              {renderStep()}
            </div>
            <aside className="hidden md:block md:col-span-4 mt-6 md:mt-0">
              <div className="panel p-4">
                <div className="section-title flex items-center gap-1.5 mb-2"><Lightbulb className="h-4 w-4 text-[hsl(var(--warning))]" />Tips</div>
                <ul className="space-y-2">{(TIPS[cur.key] || []).map((t, i) => <li key={i} className="meta flex gap-2"><span className="text-primary">•</span><span>{t}</span></li>)}</ul>
              </div>
            </aside>
          </div>
        </ScrollArea>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-border bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60">
          <div>{stepIdx > 0 && <Button variant="outline" onClick={back} data-testid="icp-wizard-back-button"><ArrowLeft className="h-4 w-4 mr-1.5" />Back</Button>}</div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="icp-wizard-cancel-button">Cancel</Button>
            {cur.key === "mode" ? (
              aiMode === "ai"
                ? <Button onClick={parse} disabled={busy || desc.trim().length < 20} data-testid="icp-wizard-ai-generate-button"><Sparkles className="h-4 w-4 mr-1.5" />{busy ? "Drafting…" : "Generate draft"}</Button>
                : <Button onClick={next} data-testid="icp-wizard-next-button">Start building<ArrowRight className="h-4 w-4 ml-1.5" /></Button>
            ) : isLast
              ? <Button onClick={save} disabled={busy || !icp.name} data-testid="icp-wizard-create-button"><Check className="h-4 w-4 mr-1.5" />{busy ? "Saving…" : editing ? "Save changes" : "Create ICP"}</Button>
              : <Button onClick={next} data-testid="icp-wizard-next-button">Next<ArrowRight className="h-4 w-4 ml-1.5" /></Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
